// src/pages/dashboard/MikrotikDashboardPage.tsx
import {
  Activity,
  AlertTriangle,
  Clock,
  Cpu,
  Database,
  Globe2,
  Loader2,
  MemoryStick,
  RadioTower,
  RefreshCw,
  Server,
  Shield,
  UserCog,
  Users,
  Wifi,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { fetchApp } from "../../services/apiService";
import type { MikrotikDashboard, RouterDashboard } from "../../types/system";

const DASHBOARD_ENDPOINT = "/system/dashboard/all";

type Tone = "emerald" | "sky" | "amber" | "violet" | "rose" | "slate" | "cyan";

const toneClasses: Record<
  Tone,
  {
    card: string;
    icon: string;
    label: string;
    ring: string;
    bar: string;
  }
> = {
  emerald: {
    card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
    icon: "bg-emerald-100 text-emerald-700",
    label: "text-emerald-700",
    ring: "bg-emerald-200/50",
    bar: "bg-gradient-to-r from-emerald-500 to-teal-500",
  },
  sky: {
    card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
    icon: "bg-sky-100 text-sky-700",
    label: "text-sky-700",
    ring: "bg-sky-200/50",
    bar: "bg-gradient-to-r from-sky-500 to-cyan-500",
  },
  amber: {
    card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
    icon: "bg-amber-100 text-amber-700",
    label: "text-amber-700",
    ring: "bg-amber-200/50",
    bar: "bg-gradient-to-r from-amber-500 to-orange-500",
  },
  violet: {
    card: "border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 shadow-violet-200/50",
    icon: "bg-violet-100 text-violet-700",
    label: "text-violet-700",
    ring: "bg-violet-200/50",
    bar: "bg-gradient-to-r from-violet-500 to-indigo-500",
  },
  rose: {
    card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
    icon: "bg-rose-100 text-rose-700",
    label: "text-rose-700",
    ring: "bg-rose-200/50",
    bar: "bg-gradient-to-r from-rose-500 to-red-500",
  },
  cyan: {
    card: "border-cyan-200 bg-gradient-to-br from-cyan-50 to-teal-50 shadow-cyan-200/50",
    icon: "bg-cyan-100 text-cyan-700",
    label: "text-cyan-700",
    ring: "bg-cyan-200/50",
    bar: "bg-gradient-to-r from-cyan-500 to-teal-500",
  },
  slate: {
    card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
    icon: "bg-slate-200 text-slate-700",
    label: "text-slate-600",
    ring: "bg-slate-200/60",
    bar: "bg-gradient-to-r from-slate-500 to-slate-600",
  },
};

const formatMiB = (value?: number) => {
  if (!value) return "0 MiB";
  return `${Math.round(value / (1024 * 1024))} MiB`;
};

const clampPercent = (value: number) => Math.min(Math.max(value || 0, 0), 100);

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  icon: React.ReactNode;
  tone?: Tone;
  progress?: number;
}> = ({ label, value, sub, icon, tone = "slate", progress }) => {
  const toneStyle = toneClasses[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${toneStyle.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${toneStyle.ring}`}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p
              className={`text-[11px] font-black uppercase tracking-widest ${toneStyle.label}`}
            >
              {label}
            </p>

            <div className="mt-1 text-2xl font-black tracking-tight text-slate-900">
              {value}
            </div>

            <div className="mt-1 text-[11.5px] font-bold leading-snug text-slate-500">
              {sub}
            </div>
          </div>

          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${toneStyle.icon}`}
          >
            {icon}
          </div>
        </div>

        {typeof progress === "number" && (
          <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full transition-all duration-500 ${toneStyle.bar}`}
              style={{ width: `${clampPercent(progress)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const HealthBar: React.FC<{
  label: string;
  value: string;
  percent: number;
  tone?: Tone;
}> = ({ label, value, percent, tone = "emerald" }) => {
  const toneStyle = toneClasses[tone];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold text-slate-600">{label}</span>
        <span className="font-black text-slate-800">{value}</span>
      </div>

      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full transition-all duration-500 ${toneStyle.bar}`}
          style={{ width: `${clampPercent(percent)}%` }}
        />
      </div>
    </div>
  );
};

const LoadingPanel: React.FC<{ message?: string }> = ({
  message = "Loading MikroTik dashboard…",
}) => (
  <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
    <div className="text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>

      <h3 className="mt-4 text-[15px] font-black text-slate-800">
        Loading Data
      </h3>

      <p className="mt-1 text-[13px] font-bold text-slate-500">{message}</p>
    </div>
  </div>
);

const MikrotikDashboardPage: React.FC = () => {
  const [routers, setRouters] = useState<RouterDashboard[]>([]);
  const [selectedRouterId, setSelectedRouterId] = useState<string | null>(null);
  const [data, setData] = useState<MikrotikDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetError = () => setError(null);

  const applySelection = (
    routerList: RouterDashboard[],
    routerId: string | null,
  ) => {
    if (!routerList.length) {
      setSelectedRouterId(null);
      setData(null);
      return;
    }

    let chosenId = routerId;

    if (
      chosenId === null ||
      !routerList.some((r) => r.router_id === chosenId)
    ) {
      const firstWithData = routerList.find((r) => r.dashboard);
      chosenId = firstWithData?.router_id ?? routerList[0]?.router_id ?? null;
      setSelectedRouterId(chosenId);
    }

    const current =
      chosenId !== null
        ? routerList.find((r) => r.router_id === chosenId)
        : undefined;

    setData(current?.dashboard ?? null);
    setError(current?.error ?? null);
  };

  const fetchDashboard = async () => {
    try {
      resetError();
      setLoading(true);

      const json = await fetchApp<RouterDashboard[]>(DASHBOARD_ENDPOINT, {
        method: "GET",
      });

      const routerList = Array.isArray(json) ? json : [];

      setRouters(routerList);
      applySelection(routerList, selectedRouterId);
    } catch (err: any) {
      setRouters([]);
      setData(null);
      setError(err?.message || "Failed to load MikroTik dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedRouter = routers.find((r) => r.router_id === selectedRouterId);

  const memoryPercent = data?.mem_usage_percent ?? 0;
  const cpuPercent = data?.cpu_load ?? 0;

  const hotspotActivePct =
    data && data.hotspot.total_users > 0
      ? Math.min(
          (data.hotspot.active_users / data.hotspot.total_users) * 100,
          100,
        )
      : 0;

  const routerHealthTone: Tone = useMemo(() => {
    if (cpuPercent >= 85 || memoryPercent >= 85) return "rose";
    if (cpuPercent >= 65 || memoryPercent >= 65) return "amber";
    return "emerald";
  }, [cpuPercent, memoryPercent]);

  const isInitialLoading = loading && !data && !routers.length && !error;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Network Control Center
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  MikroTik Overview
                </h1>

                <p className="mt-1 max-w-3xl text-[13.5px] font-medium text-slate-500">
                  Monitor router health, CPU, memory, uptime, users, PPPoE,
                  hotspots, bridges, firewall rules, pools, and active
                  connections.
                </p>

                {selectedRouter && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-1.5 text-[12px] font-bold text-slate-600">
                    <Server className="h-3.5 w-3.5 text-slate-400" />
                    Showing{" "}
                    <span className="text-slate-900">
                      {selectedRouter.router_name ||
                        `Router ${selectedRouter.router_id}`}
                    </span>
                    {selectedRouter.error && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-black text-rose-700">
                        Error
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative">
                  <select
                    className="h-10 w-full cursor-pointer appearance-none rounded-2xl border border-slate-300 bg-slate-50 px-3 pr-9 text-[13px] font-bold text-slate-700 shadow-sm transition-all focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 sm:w-64"
                    value={selectedRouterId ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const id = value ? String(value) : null;
                      setSelectedRouterId(id);
                      applySelection(routers, id);
                    }}
                    disabled={!routers.length || loading}
                  >
                    {!routers.length && (
                      <option value="">No routers available</option>
                    )}

                    {routers.map((r) => (
                      <option key={r.router_id} value={r.router_id}>
                        {r.router_name}
                        {r.error ? " – error" : ""}
                      </option>
                    ))}
                  </select>

                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                <button
                  type="button"
                  onClick={fetchDashboard}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 text-[13px] font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:from-emerald-600 hover:to-teal-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Refresh
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page loading indicator during refresh */}
        {loading && !isInitialLoading && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing MikroTik dashboard data…
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={resetError}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {isInitialLoading && <LoadingPanel />}

        {/* Empty state */}
        {!loading && !data && !error && (
          <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
                <Server className="h-8 w-8 text-slate-500" />
              </div>

              <h3 className="mt-4 text-[16px] font-black text-slate-900">
                {routers.length ? "No Dashboard Data" : "No Routers Found"}
              </h3>

              <p className="mx-auto mt-2 max-w-sm text-[13px] font-medium text-slate-500">
                {routers.length
                  ? "Dashboard data is not available for the selected router. Try refreshing or selecting a different router."
                  : "No MikroTik routers are configured for this account. Please add a router to get started."}
              </p>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Row 1 */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                label="Router Identity"
                value={data.identity || "—"}
                sub={
                  <>
                    {data.board_name} · {data.architecture}
                    <br />
                    RouterOS {data.version}
                  </>
                }
                icon={<Server className="h-5 w-5" />}
                tone="slate"
              />

              <KpiCard
                label="CPU Load"
                value={`${cpuPercent}%`}
                sub="Current CPU utilisation"
                icon={<Cpu className="h-5 w-5" />}
                tone={
                  cpuPercent >= 85 ? "rose" : cpuPercent >= 65 ? "amber" : "sky"
                }
                progress={cpuPercent}
              />

              <KpiCard
                label="Memory Usage"
                value={`${memoryPercent.toFixed(1)}%`}
                sub={`${formatMiB(data.used_memory)} used · ${formatMiB(
                  data.total_memory,
                )} total`}
                icon={<MemoryStick className="h-5 w-5" />}
                tone={
                  memoryPercent >= 85
                    ? "rose"
                    : memoryPercent >= 65
                      ? "amber"
                      : "emerald"
                }
                progress={memoryPercent}
              />

              <KpiCard
                label="Uptime"
                value={data.uptime || "—"}
                sub="Time since last reboot"
                icon={<Clock className="h-5 w-5" />}
                tone="cyan"
              />
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                label="Hotspot Users"
                value={
                  <>
                    {data.hotspot.active_users}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      active
                    </span>
                  </>
                }
                sub={`${data.hotspot.total_users} total · ${data.hotspot.total_servers} server(s)`}
                icon={<Wifi className="h-5 w-5" />}
                tone="violet"
                progress={hotspotActivePct}
              />

              <KpiCard
                label="Bridges"
                value={
                  <>
                    {data.bridges.active}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      active
                    </span>
                  </>
                }
                sub={`${data.bridges.total} total bridges`}
                icon={<RadioTower className="h-5 w-5" />}
                tone="amber"
              />

              <KpiCard
                label="Interfaces"
                value={
                  <>
                    {data.interfaces.running}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      running
                    </span>
                  </>
                }
                sub={`${data.interfaces.total} total interfaces`}
                icon={<Users className="h-5 w-5" />}
                tone="sky"
              />

              <KpiCard
                label="Router Users"
                value={
                  <>
                    {data.users.enabled}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      enabled
                    </span>
                  </>
                }
                sub={`${data.users.total} total · ${data.users.disabled} disabled`}
                icon={<UserCog className="h-5 w-5" />}
                tone="emerald"
              />
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                label="PPP & PPPoE"
                value={
                  <>
                    {data.ppp.active_sessions}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      sessions
                    </span>
                  </>
                }
                sub={`${data.ppp.total_secrets} secrets · ${data.ppp.total_profiles} profiles · ${data.ppp.total_servers} PPPoE server(s)`}
                icon={<Globe2 className="h-5 w-5" />}
                tone="cyan"
              />

              <KpiCard
                label="IP Pools"
                value={data.pools.total_pools}
                sub="Total configured address pools"
                icon={<Database className="h-5 w-5" />}
                tone="violet"
              />

              <KpiCard
                label="Firewall Rules"
                value={
                  <>
                    {data.firewall.enabled_rules}
                    <span className="ml-1 text-xs font-bold text-slate-500">
                      enabled
                    </span>
                  </>
                }
                sub={`${data.firewall.filter_rules} filter · ${data.firewall.nat_rules} NAT · ${data.firewall.mangle_rules} mangle`}
                icon={<Shield className="h-5 w-5" />}
                tone="amber"
              />

              <KpiCard
                label="Active Connections"
                value={data.connections.active_connections}
                sub={
                  <>
                    Current entries in{" "}
                    <span className="font-mono">/ip firewall connection</span>
                  </>
                }
                icon={<Activity className="h-5 w-5" />}
                tone="emerald"
              />
            </div>

            {/* Router Health */}
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-100/80 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-2xl ${toneClasses[routerHealthTone].icon}`}
                  >
                    <Activity className="h-5 w-5" />
                  </div>

                  <div>
                    <p className="text-[14px] font-black text-slate-900">
                      Router Health
                    </p>

                    <p className="text-[11.5px] font-medium text-slate-500">
                      Quick view of key utilisation metrics.
                    </p>
                  </div>
                </div>

                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-black ${
                    routerHealthTone === "emerald"
                      ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                      : routerHealthTone === "amber"
                        ? "border-amber-200 bg-amber-100 text-amber-800"
                        : "border-rose-200 bg-rose-100 text-rose-700"
                  }`}
                >
                  {routerHealthTone === "emerald"
                    ? "Healthy"
                    : routerHealthTone === "amber"
                      ? "Needs attention"
                      : "High usage"}
                </span>
              </div>

              <div className="space-y-4 p-5">
                <HealthBar
                  label="CPU Utilisation"
                  value={`${cpuPercent}%`}
                  percent={cpuPercent}
                  tone={
                    cpuPercent >= 85
                      ? "rose"
                      : cpuPercent >= 65
                        ? "amber"
                        : "sky"
                  }
                />

                <HealthBar
                  label="Memory Utilisation"
                  value={`${memoryPercent.toFixed(1)}%`}
                  percent={memoryPercent}
                  tone={
                    memoryPercent >= 85
                      ? "rose"
                      : memoryPercent >= 65
                        ? "amber"
                        : "emerald"
                  }
                />

                <HealthBar
                  label="Hotspot Active Users"
                  value={`${data.hotspot.active_users}/${
                    data.hotspot.total_users || 0
                  }`}
                  percent={hotspotActivePct}
                  tone="violet"
                />
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default MikrotikDashboardPage;
